import React, { useEffect, useRef } from 'react';

/**
 * Reusable SidebarPanel component for displaying content in a right-side sliding panel.
 *
 * Props:
 * - isOpen: boolean - Controls whether the sidebar is visible
 * - onClose: function - Callback when sidebar should close (clicking overlay or close button)
 * - title: string | ReactNode - Title displayed in the sidebar header
 * - headerActions: ReactNode - Optional actions to display in the header (e.g., Edit button)
 * - children: ReactNode - Content to display in the sidebar body
 * - showBackButton: boolean - Whether to show a back arrow button (default: false)
 * - showCloseButton: boolean - Whether to show the X close button (default: true)
 * - className: string - Additional class name for the sidebar panel
 * - style: object - Inline styles to apply to the sidebar panel
 * - disableAnimation: boolean - Whether to disable slide animation (default: false)
 */
const SidebarPanel = ({
  isOpen,
  onClose,
  title,
  headerActions,
  children,
  showBackButton = false,
  showCloseButton = true,
  className = '',
  style = {},
  disableAnimation = false
}) => {
  const sidebarRef = useRef(null);
  const wasOpenRef = useRef(false);

  // Effect to add/remove 'open' class for sidebar animation
  useEffect(() => {
    if (sidebarRef.current) {
      if (isOpen) {
        // If animation is disabled, disable transition (works on first open and subsequent toggles)
        if (disableAnimation) {
          // Temporarily disable transition
          const currentTransition = sidebarRef.current.style.transition;
          sidebarRef.current.style.transition = 'none';

          // Ensure the class is there
          if (!sidebarRef.current.classList.contains('open')) {
            sidebarRef.current.classList.add('open');
          }

          // Force reflow to apply the style change
          sidebarRef.current.offsetHeight;

          // Restore transition after a small delay
          requestAnimationFrame(() => {
            if (sidebarRef.current) {
              sidebarRef.current.style.transition = currentTransition;
            }
          });
        } else {
          // Small delay to ensure the element is in the DOM before adding the class
          requestAnimationFrame(() => {
            sidebarRef.current?.classList.add('open');
          });
        }
        wasOpenRef.current = true;
      } else {
        sidebarRef.current?.classList.remove('open');
        wasOpenRef.current = false;
      }
    }
  }, [isOpen, disableAnimation]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <div className="sidebar-overlay" onClick={onClose}>
      <div
        ref={sidebarRef}
        className={`sidebar-panel ${className}`.trim()}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sidebar-header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            {showBackButton && (
              <button 
                className="btn btn-sm btn-light me-2"
                onClick={onClose}
                title="Close"
              >
                <i className="fa fa-arrow-left"></i>
              </button>
            )}
            <span className="fs-4 mb-0">
              {title}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {headerActions}
            {showCloseButton && (
              <button 
                type="button" 
                className="sidebar-close-btn" 
                onClick={onClose}
                title="Close"
              >
                &times;
              </button>
            )}
          </div>
        </div>
        <div className="sidebar-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default SidebarPanel;
